'use client';

import React from 'react';
import { X } from 'lucide-react';
import { format } from 'date-fns';
import type { DocumentType } from '@paeffgen/react/crm-documents/data-access-graphql';
import {
  type FilterState,
  EMPTY_FILTER_STATE,
  DATE_RANGE_LABELS,
} from './document-filters';

interface DocumentFilterChipsProps {
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
}

/** Enum-Wert → lesbares Label (gleiche Konvention wie die Typ-Cards). */
function typeLabel(type: DocumentType): string {
  return type.charAt(0) + type.slice(1).toLowerCase();
}

function formatDate(value?: string): string {
  if (!value) return '';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : format(parsed, 'dd.MM.yyyy');
}

function amountLabel(min?: string, max?: string): string {
  if (min && max) return `${min}–${max} €`;
  if (min) return `ab ${min} €`;
  return `bis ${max} €`;
}

/**
 * Aktive-Filter-Chips für die Beleg-Liste. Ersetzt die frühere
 * „x Filter aktiv"-Zeile und spiegelt das Chip-Design der Artikelsuche
 * (article-search-filters.tsx): weißes Chip, oranger Rand, X zum
 * Einzel-Entfernen, „Alle zurücksetzen" rechts.
 */
export function DocumentFilterChips({
  filters,
  onFilterChange,
}: DocumentFilterChipsProps) {
  const chips: { key: string; label: string; onRemove: () => void }[] = [];

  if (filters.search) {
    chips.push({
      key: 'search',
      label: `Suche: "${filters.search}"`,
      onRemove: () => onFilterChange({ ...filters, search: '' }),
    });
  }

  for (const type of filters.type) {
    chips.push({
      key: `type-${type}`,
      label: `Typ: ${typeLabel(type)}`,
      onRemove: () =>
        onFilterChange({
          ...filters,
          type: filters.type.filter((t) => t !== type),
        }),
    });
  }

  if (filters.dateRange !== 'ALL') {
    const label =
      filters.dateRange === 'CUSTOM'
        ? `Zeit: ${formatDate(filters.startDate)}–${formatDate(filters.endDate)}`
        : `Zeit: ${DATE_RANGE_LABELS[filters.dateRange]}`;
    chips.push({
      key: 'dateRange',
      label,
      onRemove: () => {
        const next: FilterState = { ...filters, dateRange: 'ALL' };
        delete next.startDate;
        delete next.endDate;
        onFilterChange(next);
      },
    });
  }

  if (filters.minAmount || filters.maxAmount) {
    chips.push({
      key: 'amount',
      label: `Betrag: ${amountLabel(filters.minAmount, filters.maxAmount)}`,
      onRemove: () => onFilterChange({ ...filters, minAmount: '', maxAmount: '' }),
    });
  }

  if (filters.location) {
    chips.push({
      key: 'location',
      label: `Standort: ${filters.location}`,
      onRemove: () => onFilterChange({ ...filters, location: '' }),
    });
  }

  if (filters.commission) {
    chips.push({
      key: 'commission',
      label: `Kommission: ${filters.commission}`,
      onRemove: () => onFilterChange({ ...filters, commission: '' }),
    });
  }

  if (filters.yourReference) {
    chips.push({
      key: 'yourReference',
      label: `Referenz: ${filters.yourReference}`,
      onRemove: () => onFilterChange({ ...filters, yourReference: '' }),
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
        Aktive Filter:
      </span>
      {chips.map((chip) => (
        <span
          key={chip.key}
          className="inline-flex items-center gap-1 px-2.5 py-1 bg-white text-gray-800 text-sm font-medium rounded-md border border-orange-200 shadow-sm"
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onRemove}
            aria-label={`Filter entfernen: ${chip.label}`}
            className="text-gray-400 hover:text-red-500 ml-1"
          >
            <X size={14} />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={() => onFilterChange(EMPTY_FILTER_STATE)}
        className="text-xs font-medium text-orange-700 hover:text-orange-900 hover:underline ml-2"
      >
        Alle zurücksetzen
      </button>
    </div>
  );
}
